import React, { Fragment } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { CostType } from '../../../generated/graphql'
import { CostRowDataForPrefill, getPrefilledData } from './cost-table-util'
import { SelectedVersion } from '../../layout/VersionsSelector'
import { reduceToSum } from '../../../util/array-utility'
import { formatPrice } from '../../../util/formatters'
import { CostsTableValueRow } from './CostsTableValueRow'
import { useTableStyles } from '../../common/use-table-styles'
import type {
  GroupedCost,
  FixedCostRow,
  GroupedFixedCost,
} from './cost-table-types'
import { CostsTableVersionNameRow } from './CostsTableVersionNameRow'

type Props = {
  groupedCosts: GroupedFixedCost[]
  type: CostType
  versions: SelectedVersion[]
  updateCost: (costId: string, versionId: string) => void
  createNewCost: (
    costRowData: CostRowDataForPrefill,
    versionId: string,
    groupedCostName: string,
  ) => void
}

export function FixedCostsTable(props: Props): React.ReactElement {
  const { groupedCosts, type, versions, updateCost, createNewCost } = props
  const classes = useTableStyles()
  const { t } = useTranslation()

  const getVersionSum = (versionId: string, costs: GroupedCost[]): string => {
    const sum = costs
      .flatMap((groupedCost) => groupedCost.costRows)
      .flatMap((row) => row.costs)
      .filter((cost) => cost.versionId === versionId)
      .map((cost) =>
        cost.multiplier ? cost.value * cost.multiplier : cost.value,
      )
      .reduce(reduceToSum, 0)

    return formatPrice(sum, { omitUnit: true })
  }

  const handleCostClick = (
    versionId: string,
    costRow: FixedCostRow,
    groupedCostName: string,
  ) => {
    const { costs } = costRow
    const cost = costs.find(
      (costRowCost) => costRowCost.versionId === versionId,
    )
    if (!cost) {
      createNewCost(
        getPrefilledData(versions, costRow),
        versionId,
        groupedCostName,
      )
      return
    }
    updateCost(cost.id, versionId)
  }

  return (
    <TableContainer>
      <Table className={classes.table}>
        <TableBody>
          {groupedCosts.length === 0 ? (
            <CostsTableVersionNameRow versions={versions} />
          ) : (
            groupedCosts.map((groupedCost) => (
              <Fragment key={`${type}-${groupedCost.name}`}>
                <TableRow>
                  <TableCell className={classes.titleHeaderCell}>
                    {groupedCost.name
                      ? groupedCost.name
                      : t('costs.table.noProvider')}
                  </TableCell>
                  {versions.map((version) => (
                    <TableCell
                      className={classes.versionHeaderCell}
                      key={`${type}-${version.id}`}
                      align="center"
                    >
                      {version.name}
                    </TableCell>
                  ))}
                </TableRow>
                {groupedCost.costRows.map((costRow) => (
                  <CostsTableValueRow
                    key={`${type}-${costRow.providerName}-${costRow.description}`}
                    costRow={costRow}
                    handleCostClick={handleCostClick}
                    versions={versions}
                    type={type}
                    title={groupedCost.name}
                  />
                ))}
              </Fragment>
            ))
          )}
          <TableRow>
            <TableCell className={classes.titleHeaderCell}>
              {t('costs.table.sum')}
            </TableCell>
            {versions.map((version) => (
              <Fragment key={`sum-${version.id}`}>
                <TableCell className={classes.versionHeaderCell} align="center">
                  {getVersionSum(version.id, groupedCosts)}
                </TableCell>
              </Fragment>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}
